import { Component, computed, inject, signal } from '@angular/core';
import { UploadTrackerService } from './upload-tracker.service';
import { UploadTask } from './upload-tracker.models';

@Component({
  selector: 'app-upload-tracker',
  standalone: true,
  template: `
    @if (tracker.hasVisibleTasks()) {
      <div class="jl-ul-container" [@.disabled]="prefersReducedMotion">
        @if (minimized()) {
          <!-- Minimized pill -->
          <button class="jl-ul-pill" (click)="minimized.set(false)">
            <i class="pi pi-upload jl-ul-pill-icon"></i>
            <span class="jl-ul-pill-text">{{ progress().completed }} / {{ progress().total }}</span>
            <div class="jl-ul-pill-bar">
              <div class="jl-ul-pill-bar-fill" [style.width.%]="progressPercent()"></div>
            </div>
          </button>
        } @else {
          <!-- Maximized card -->
          <div class="jl-ul-card jl-ul-slide-in">
            <div class="jl-ul-header">
              <span class="jl-ul-header-title">Yükleme İşlemi</span>
              <div class="jl-ul-header-actions">
                <button class="jl-ul-header-btn" (click)="minimized.set(true)" title="Küçült">
                  <i class="pi pi-minus"></i>
                </button>
                <button class="jl-ul-header-btn" (click)="dismissAll()" title="Kapat">
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
            <div class="jl-ul-body">
              @for (task of tracker.tasks(); track task.id) {
                <div class="jl-ul-task">
                  <div class="jl-ul-task-header">
                    <span class="jl-ul-task-label">{{ task.label }}</span>
                    <span class="jl-ul-task-count">{{ task.completedFiles }} / {{ task.totalFiles }}</span>
                  </div>
                  <div class="jl-ul-progress-bar">
                    <div
                      class="jl-ul-progress-fill"
                      [class.jl-ul-progress-fill--failed]="task.status === 'failed'"
                      [class.jl-ul-progress-fill--cancelled]="task.status === 'cancelled'"
                      [style.width.%]="taskPercent(task)"
                    ></div>
                  </div>
                  <div class="jl-ul-task-footer">
                    @switch (task.status) {
                      @case ('running') {
                        <span class="jl-ul-status-text">{{ task.files[task.currentFileIndex]?.name ?? '' }} yükleniyor...</span>
                        <button class="jl-ul-action-btn jl-ul-action-btn--cancel" (click)="tracker.cancelTask(task.id)">İptal Et</button>
                      }
                      @case ('completed') {
                        <span class="jl-ul-status-text jl-ul-status-text--success">Tamamlandı</span>
                        <button class="jl-ul-action-btn jl-ul-action-btn--dismiss" (click)="tracker.dismissTask(task.id)">Kaldır</button>
                      }
                      @case ('failed') {
                        <span class="jl-ul-status-text jl-ul-status-text--error">Başarısız oldu</span>
                        <div class="jl-ul-action-group">
                          <button class="jl-ul-action-btn jl-ul-action-btn--retry" (click)="tracker.retryTask(task.id)">Tekrar Dene</button>
                          <button class="jl-ul-action-btn jl-ul-action-btn--cancel" (click)="tracker.cancelTask(task.id)">İptal Et</button>
                        </div>
                      }
                      @case ('cancelled') {
                        <span class="jl-ul-status-text jl-ul-status-text--muted">İptal edildi</span>
                        <button class="jl-ul-action-btn jl-ul-action-btn--dismiss" (click)="tracker.dismissTask(task.id)">Kaldır</button>
                      }
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    /* ── Minimized pill ── */
    .jl-ul-pill {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.875rem;
      background: var(--color-surface-0);
      border: 1px solid var(--color-border);
      border-radius: 2rem;
      box-shadow: var(--shadow-lg);
      cursor: pointer;
      transition: background 0.15s ease;
    }
    .jl-ul-pill:hover {
      background: var(--color-surface-1);
    }
    .jl-ul-pill-icon {
      font-size: 0.875rem;
      color: var(--color-primary);
    }
    .jl-ul-pill-text {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--color-text);
    }
    .jl-ul-pill-bar {
      width: 3.5rem;
      height: 0.25rem;
      background: var(--color-surface-2);
      border-radius: 0.125rem;
      overflow: hidden;
    }
    .jl-ul-pill-bar-fill {
      height: 100%;
      background: var(--color-primary);
      border-radius: 0.125rem;
      transition: width 0.3s ease;
    }

    /* ── Maximized card ── */
    .jl-ul-card {
      width: 22rem;
      background: var(--color-surface-0);
      border: 1px solid var(--color-border);
      border-radius: 0.75rem;
      box-shadow: var(--shadow-lg);
      overflow: hidden;
    }
    .jl-ul-slide-in {
      animation: jl-ul-slide-in 0.2s ease-out;
    }
    @keyframes jl-ul-slide-in {
      from {
        opacity: 0;
        transform: translateY(0.75rem);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .jl-ul-slide-in {
        animation: none;
      }
    }

    .jl-ul-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--color-border);
    }
    .jl-ul-header-title {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--color-text);
    }
    .jl-ul-header-actions {
      display: flex;
      gap: 0.25rem;
    }
    .jl-ul-header-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: transparent;
      border: none;
      border-radius: 0.25rem;
      color: var(--color-text-secondary, #888);
      cursor: pointer;
      transition: background 0.15s ease;
    }
    .jl-ul-header-btn:hover {
      background: var(--color-surface-2);
    }
    .jl-ul-header-btn i {
      font-size: 0.75rem;
    }

    .jl-ul-body {
      max-height: 16rem;
      overflow-y: auto;
      padding: 0.5rem 0;
    }

    /* ── Task row ── */
    .jl-ul-task {
      padding: 0.625rem 1rem;
    }
    .jl-ul-task + .jl-ul-task {
      border-top: 1px solid var(--color-border);
    }
    .jl-ul-task-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.375rem;
    }
    .jl-ul-task-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--color-text);
    }
    .jl-ul-task-count {
      font-size: 0.75rem;
      color: var(--color-text-secondary, #888);
      font-variant-numeric: tabular-nums;
    }

    /* ── Progress bar ── */
    .jl-ul-progress-bar {
      height: 0.375rem;
      background: var(--color-surface-2);
      border-radius: 0.1875rem;
      overflow: hidden;
      margin-bottom: 0.375rem;
    }
    .jl-ul-progress-fill {
      height: 100%;
      background: var(--color-primary);
      border-radius: 0.1875rem;
      transition: width 0.3s ease;
    }
    .jl-ul-progress-fill--failed {
      background: var(--color-danger, #ef4444);
    }
    .jl-ul-progress-fill--cancelled {
      background: var(--color-text-secondary, #888);
    }

    /* ── Footer ── */
    .jl-ul-task-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .jl-ul-status-text {
      font-size: 0.75rem;
      color: var(--color-text-secondary, #888);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 14rem;
    }
    .jl-ul-status-text--success {
      color: var(--color-success, #22c55e);
    }
    .jl-ul-status-text--error {
      color: var(--color-danger, #ef4444);
    }
    .jl-ul-status-text--muted {
      color: var(--color-text-secondary, #888);
    }

    .jl-ul-action-group {
      display: flex;
      gap: 0.5rem;
    }
    .jl-ul-action-btn {
      font-size: 0.75rem;
      font-weight: 500;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
    }
    .jl-ul-action-btn--retry {
      color: var(--color-primary);
    }
    .jl-ul-action-btn--retry:hover {
      text-decoration: underline;
    }
    .jl-ul-action-btn--cancel {
      color: var(--color-danger, #ef4444);
    }
    .jl-ul-action-btn--cancel:hover {
      text-decoration: underline;
    }
    .jl-ul-action-btn--dismiss {
      color: var(--color-text-secondary, #888);
    }
    .jl-ul-action-btn--dismiss:hover {
      text-decoration: underline;
    }
  `],
})
export class UploadTrackerComponent {
  protected readonly tracker = inject(UploadTrackerService);
  protected readonly minimized = signal(false);

  protected readonly prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  protected readonly progress = computed(() => this.tracker.globalProgress());

  protected readonly progressPercent = computed(() => {
    const p = this.progress();
    return p.total > 0 ? Math.round((p.completed / p.total) * 100) : 0;
  });

  protected taskPercent(task: UploadTask): number {
    return task.totalFiles > 0 ? Math.round((task.completedFiles / task.totalFiles) * 100) : 0;
  }

  protected dismissAll(): void {
    for (const task of this.tracker.tasks()) {
      this.tracker.dismissTask(task.id);
    }
  }
}
